import { ReactNode } from "react";

import {
  CONDITION_OPERATOR,
  DEFAULT_DASHBOARD,
  DOCUMENT_SCOPE,
  FOLDER_TYPE,
  LIFE_CYCLE_ENTITY,
  LIST_SCOPE,
  META_EXPRESSION_SCOPE,
  NUMBER_FILTER,
  OPERATION_OPERATOR,
  PACK_ID,
  PLAN_ID,
  STOCK_MOVEMENT_MODE,
} from "fieldpro-tools";

import { DASHBOARD_STATUS_TYPE } from "model/entities/Dashboard";

export type THoverToolTipsLang = {
  important: string;
  locked: string;
  disabled: string;
  hidden: string;
  mandatory: string;
  unique: string;
  hasQuery: string;
};

export enum ENTITIES {
  CLIENT = "clients",
  DASHBOARD = "dashboards",
  DETECT = "detect",
  LIST = "lists",
  TEAM = "teams",
  USER = "users",
  WORKFLOW = "workflows",
  PICTURE = "pictures",
  DOCUMENT = "documents",
  JOB = "jobs",
  TRIGGER = "triggers",
  ORDER = "orders",
  WEBHOOK = "webhooks",
  ENVIRONMENT = "environment",
  CALENDAR = "calendar",
  ROUTE = "routes",
}

export enum SUB_CATEGORIES {
  CLIENT = "clients",
  CLIENT_SUBSCRIPTION = "subscriptions",
  CLIENT_BILLING = "billing",
  CLIENT_PLAN = "plans",
  CLIENT_PACK = "packs",
  DASHBOARD = "dashboards",
  LIST = "lists",
  ITEM = "items",
  TERRITORY = "territories",
  TEAM = "teams",
  GROUP = "groups",
  MOBILE_USER = "mobileUsers",
  WEB_USER = "webUsers",
  WORKFLOW = "workflows",
  ACTIVITY = "activities",
  ACTIVITY_REPORT = "activityReports",
  JOB_HISTORY = "jobHistory",
  PICTURE = "pictures",
  DOCUMENT = "documents",
  TRANSFORMATION = "transformations",
  TABLEOPTIMIZATION = "tableOptimizations",
  NOTIFICATION = "notifications",
  HTTP_REQUEST = "http_requests",
  INTEGRATION = "integrations",
  STREAM = "streams",
  SCRIPT = "scripts",
  JOB = "jobs",
  TRIGGER = "triggers",
  WEBHOOK = "webhooks",
  META_EXPRESSION = "metaExpressions",
  WORKFLOW_REPORT = "workflowReports",
  DATABASE_SCHEMA = "databaseSchema",
  FOLDER = "folders",
  CALENDAR_EVENT = "calendarEvent",
  CATALOGUE = "catalogue",
}

// all the custom actions where cannot be generalise (all the "standard" actions are in lang.actions)
export type TCustomDirectActionLabel = {
  bulkLicense: string;
  bulkCreate: string;
  bulkUnlicense: string;
  bulkArchive: string;
  bulkUnassign: string;
  bulkUnassignMobileUsers: string;
  bulkEdit: string;
  bulkApproval: string;
  bulkRejection: string;
  bulkBackToPending: string;
  bulkRestore: string;
  bulkDelete: string;
  bulkDeleteMobileUser: string;
  bulkDownloadQRCodes: string;
  assignationActions: string;
  bulkOperations: string;
  dataDownloads: string;
  swapItemsOwners: string;
  assignItemsOwners: string;
  clearAssignations: string;
  unassignItemsOwners: string;
  downloadKmlListFile: string;
  downloadGeojsonFile: string;
  select: string;
  detail: string;
  archive: string;
  download?: string;
  refresh?: string;
  export?: string;
  fetchRawData: string;
  restoreVersion: string;
  findDuplicates: string;
  openDetailPanel: string;
  manageHierarchy: string;
  bulkEditLevelsCSV: string;
  openConfigurationPanel: string;

  bulkArchiveWithCSV: string;
  bulkRestoreWithCSV: string;
  bulkEditWithCSV: string;
  bulkDeleteWithCSV: string;
  bulkUnassignWithCSV: string;
  bulkAssignWithCSV: string;

  bulkAssignWebUsersToTeams: string;
  bulkAssignWorkflowsToTeams: string;
  bulkAssignMobileUsersToTeams: string;
  bulkUnAssignWebUsersFromTeams: string;
  bulkUnAssignMobileUsersFromTeams: string;
};

export enum ERROR_LIST {
  EMPTY = "empty",
  MANDATORY_FIELD = "mandatoryField",
  TOO_SHORT = "tooShort",
  NOT_POSSIBLE_TO_MODIFY_BECAUSE_TEMPLATE = "notPossibleToModifyBecauseTemplate",
  SIMILAR_TAG_WARNING = "similarTagWarning",
  TOO_LONG = "tooLong",
  EXCEEDS_MAX_LENGTH = "exceedsMaxLength",
  START_WITH_LETTER = "startWithLetter",
  ALPHANUMERIC = "alphanumeric",
  ALPHANUMERIC_META_EXPRESSION = "alphanumericMetaExpression",
  ALREADY_IN_USE = "alreadyInUse",
  WRONG_FORMAT = "wrongFormat",
  IMPOSSIBLE_TO_FIND = "impossibleToFind",
  MAXIMUM = "maximum",
  MINIMUM = "minimum",
  OPTION_FORBIDDEN_CHARACTERS = "optionLabelForbiddenCharacters",
  UNLINKED_STEPS = "unlinkedSteps",
  NOT_SECURED_ENOUGH = "notSecuredEnough",
  MORE_THAN_ONE_START_WORFKLOW = "moreThanOneStartWorkflow",
  IN_DEPRECATED = "inDeprecated",
  EMAIL_CONTAIN_UPPERCASE = "emailContainUppercase",
  TEMPLATE_TAGS_REQUIRE_PREFIX = "templateTagsRequirePrefix",
  TEMPLATE_TAGS_USED_AS_METADATA = "templateTagsIsUsedAsMetadata",
  NO_VALID_ID = "invalidId",
  INVALID_CUSTOM_ID = "invalidCustomId",
  QUESTION_VALIDATION_ERROR = "questionValidationError",
  INVALID_PHONE_NUMBER = "invalidPhoneNumber",
  INVALID_EMAIL = "invalidEmail",
  INVALID_EMAIL_FORMAT = "invalidEmailFormat",
  INVALID_NAME = "invalidName",
  INVALID_NUMBER = "invalidNumber",
  EMAIL_CONTAIN_ALIAS = "emailContainAlias",
}

export interface TCustomMessageLang {
  [key: string]: any;
}

export type TInputAttributeLang = {
  title: string;
  defaultTitle: string;
  description: string;
  tooltip: string;
  placeholder?: string;
  options: {
    [optionName: string]: string;
  };
  customErrorMessages: {
    [errorMessage: string]: string;
  };
  customMessage: TCustomMessageLang;
  templates: any;
  selectModal: TSelectModalLang;
  errorOnRemove: TConfirmModalLang;
  warningOnRemove: TConfirmModalLang;
  warningMessage: TConfirmModalLang;
  createEditModal: TCreateEditModalLang & {
    changeType: string;
  };
  hoverToolTips: THoverToolTipsLang;
};

export type TConfirmModalLang = {
  title?: string;
  description?: ReactNode;
};

export type TSelectModalLang = {
  title: string;
  description?: string;
};

export type TWebhookScreenLang = {
  tabs: {
    schemaTab: string;
    settingsTab: string;
  };
  sidebar: {
    createSchemaButton: string;
  };
  empty: {
    title: string;
    description: string;
  };
};

export type TActivityScreenLang = {
  addSubquestionButton: string;
  customerSelection: string;
  assignSelection: string;
  changeTypeButton: string;
  questionType: string;
  modal: {
    newQuestion: string;
    editQuestion: string;
    viewQuestion: string;
    duplicateQuestion: string;
    typeQuestion: {
      deprecatedSoonBadge: string;
      newBadge: string;
      title: string;
      searchPlaceholder: string;
    };
  };
  sidebar: {
    download: {
      inJson: string;
      inCsv: string;
    };
    createQuestionButton: string;
    uploadJSONButton: string;
    activityAlternativeText: string;
    downloadDescription: string;
    jsonOperations: string;
    showDeletedQuestions: string;
  };
  errors: {
    atLeastOneSubQuestion: string;
    atLeastOneQuestion: string;
    activityWithSameName: string;
  };
  tabs: {
    questionsTab: string;
    settingsTab: string;
    previewTab: string;
  };
  empty: {
    title: string;
    description: string;
  };
  hoverToolTips: THoverToolTipsLang;
};

export type TWarningModalLang = {
  title: string;
  description: string;
};

export type TLifecycleModalsLang = {
  entitiesOverview: {
    title: string;
    subtitle: string;
    entityDescriptions: {
      ORDER: string;
      ORDER_DELIVERY: string;
      STOCK_MOVEMENT: string;
      CUSTOMER: string;
    };
    stats: {
      statuses: string;
      transitions: string;
      rules: string;
    };
  };
  statusEditor: {
    title: string;
    addButton: string;
    labels: {
      id: string;
      entity: string;
      textColor: string;
      editableBy: string;
      web: string;
      mobile: string;
    };
  };
  transitionsEditor: {
    title: string;
    addButton: string;
    labels: {
      tasks: string;
      task: string;
      tasks_plural: string;
      allowedRoles: string;
      web: string;
      mobile: string;
    };
  };
  creationRulesEditor: {
    title: string;
    addButton: string;
    labels: {
      statusId: string;
      tasks: string;
      task: string;
      tasks_plural: string;
      defaultRule: string;
      restrictedToRoles: string;
      web: string;
      mobile: string;
    };
  };
  statusModal: {
    createTitle: string;
    editTitle: string;
    inputLabel: TInputAttributeLang & {
      error: string;
    };
    inputId: TInputAttributeLang & {
      error: string;
    };
    inputEntity: TInputAttributeLang;
    inputTextColor: TInputAttributeLang;
    inputBackgroundColor: TInputAttributeLang;
    editableBySection: {
      title: string;
    };
    inputWebUserRoles: TInputAttributeLang;
    inputMobileUserRoles: TInputAttributeLang;
  };
  creationRuleModal: {
    createTitle: string;
    editTitle: string;
    inputDefaultStatus: TInputAttributeLang & {
      error: string;
    };
    inputWebUserRoles: TInputAttributeLang;
    inputMobileUserRoles: TInputAttributeLang;
    tasksSection: {
      title: string;
      addButton: string;
    };
  };
  taskModal: {
    createTitle: string;
    editTitle: string;
    inputTaskId: TInputAttributeLang & {
      error: string;
      errorDuplicate: string;
    };
    parametersSection: {
      title: string;
      addButton: string;
    };
    inputParameterKey: TInputAttributeLang & {
      error: string;
    };
    inputParameterType: TInputAttributeLang;
    inputParameterDays: TInputAttributeLang & {
      placeholder: string;
    };
    inputParameterStatusValue: TInputAttributeLang;
  };
  transitionModal: {
    createTitle: string;
    editTitle: string;
    inputFromStatus: TInputAttributeLang & {
      error: string;
    };
    inputToStatus: TInputAttributeLang & {
      error: string;
      errorDifferent: string;
      errorDuplicate: string;
    };
    inputWebUserRoles: TInputAttributeLang;
    inputMobileUserRoles: TInputAttributeLang;
    tasksSection: {
      title: string;
      addButton: string;
    };
  };
};

export type TCreateEditModalLang = {
  createTitle: string;
  editTitle: string;

  notFound?: string;
  section?: { [sectionName: string]: string };
  title?: string;
  description?: string;
  submitButton?: string;
  save: string;
  cancel: string;
  createWarningModal?: TWarningModalLang;
  lifecycleModals?: TLifecycleModalsLang;
} & {
  [inputAttName: string]: TInputAttributeLang;
};

export type TRunJobModalLang = {
  jobTitle: string;
  title?: string;
  description?: string;
} & {
  [inputAttName: string]: TInputAttributeLang;
};

export type TLockModalLang = {
  title: string;
  description: string;
} & {
  [inputAttName: string]: TInputAttributeLang;
};

export type TArchiveDeleteModalLang = {
  title: string;
  description: string;
  inputName: TInputAttributeLang;
  warningModal: TConfirmModalLang;
};

export enum LANG_GENERIC_TERMS {
  YES = "yes",
  NO = "no",
  NONE = "none",
  NOT_AVAILABLE_SHORT = "notAvailableShort",
  NOT_FOUND = "notFound",
  NOTIFICATIONS = "notifications",
  TO = "to",
  NEXT = "next",
  PREVIOUS = "previous",
  SAVE = "save",
  SAVE_CHANGES = "saveChanges",
  DISCARD = "discard",
  DISCARD_CHANGES = "discardChanges",
  STOP_EXECUTION = "stopExecution",
  RESUME_EXECUTION = "resumeExecution",
  CONTINUE_EXECUTION = "continueExecution",
  CANCEL = "cancel",
  CREATE = "create",
  DOWNLOAD = "download",
  RELOAD = "reload",
  LOADING = "loading",
  VIEW = "view",
  HELP = "help",
  RELEASES = "releases",
  MANDATORY = "mandatory",
  ARCHIVE = "archive",
  BULK_ARCHIVE = "bulkArchive",
  DELETE = "delete",
  BULK_DELETE = "bulkDelete",
  EDIT = "edit",
  LICENSED = "licensed",
  LICENSE = "license",
  LICENSES = "licenses",
  UNLICENSED = "unlicensed",
  REFRESH = "refresh",
  REPLICATE = "replicate",
  RESTORE = "restore",
  BULK_RESTORE = "bulkRestore",
  STOP = "stop",
  YES_ACTION = "yes_action",
  NO_ACTION = "no_action",
  RANGE_A_TO_B = "rangeAtoB",
  RANGE_A_TO_B_OF_C = "rangeAtoBofC",
  EDIT_DETAILS = "editDetails",
  EXPAND = "expand",
  COLLAPSE = "collapse",
  OPERATORS = "operators",
  CONTENT = "content",
  ARCHIVED = "archived",
  VALIDATE = "validate",
  PERIOD = "period",
  PERIODSHORT = "periodShort",
  PERIODS = "periods",
  PERIODSSHORT = "periodsShort",
  TERMS_AND_CONDITIONS = "termsandConditions",
  PRIVACY_POLICY = "privacyPolicy",
  LOGIN = "login",
  SIGNUP = "signup",
  COMING_SOON = "comingSoon",
  SELECT_AN_OPTION = "selectAnOption",
  SPLIT_BUTTON = "splitButton",
  COPY_IN_CLIPBOARD = "copyInClipboard",
  DEFAULT = "default",
  SAVE_X_PERCENT = "SAVE_X%",
  SELECTALL = "selectAll",
  SELECTNONE = "selectNone",
  NOTOPTIONSAVAILABLE = "noOptionsAvailable",
  UNKNOWN = "unknown",
  UNKNOWN_WITH_ID = "unknownWithId",
  All = "All",
  PER_X = "PER_X",
  CUMULATIVE = "cumulative",
  COUNT = "count",
  DENSITY = "density",
  METADATA = "metadata",
  FORM_ERROR = "formError",
  DOWNLOAD_OPTIONS = "downloadOptions",
  APPROVE = "approve",
  REJECT = "reject",
  DECLINE = "decline",
  ACCEPT = "accept",
  BACK_TO_PENDING = "backToPending",
}

export enum LANG_ACTIONS {
  ADD = "add",
  BACK = "back",
  BUY_LICENSES = "buyLicenses",
  CANCEL = "cancel",
  CONTINUE = "continue",
  CREATE = "create",
  EDIT = "edit",
  ARCHIVE = "archive",
  RESTORE = "restore",
  DOWNGRADE = "downgrade",
  DOWNLOAD = "download",
  RELOAD = "reload",
  RESTART = "restart",
  HELP = "help",
  DELETE = "delete",
  FETCH = "fetch",
  DISPLAY = "display",
  UPLOAD = "upload",
  LICENSE = "license",
  UNLICENSE = "unlicense",
  ASSIGN = "assign",
  UNASSIGN = "unassign",
  RUN = "run",
  STOP = "stop",
  CLOSE = "close",
  REPLICATE = "replicate",
  RESUME = "resume",
  DUPLICATE = "duplicate",
  RESET_PASSWORD = "resetPassword",
  DOWNLOAD_EXCEL = "downloadExcel",
  DOWNLOAD_ZIP = "downloadZip",
  DOWNLOAD_CSV = "downloadCSV",
  UPGRADE = "upgrade",
  WRITE_TO_US = "writeToUs",
  LOAD_MORE = "loadMore",
  SEND = "send",
  CHANGE_TAG = "changeTag",
}

export enum LANG_COMPONENTS {
  TABLE = "table",
  SEARCH = "search",
  ERROR = "errors",
  EXPANSION_PANEL = "expansionPanel",
  FILTERS = "filters",
  META_EXPRESSIONS = "metaExpressions",
  INPUT_ERRORS = "inputErrors",
  UPDATE_MONITOR = "updateMonitor",
  INPUT_CONDITION = "inputCondition",
  INPUT_OPERATION = "inputOperation",
  MAPS = "maps",
  CARD = "card",
  COLUMNS = "columns",
  DIALOG = "dialog",
  EMPTY_TABLE = "emptyTable",
  INPUT_SELECT = "inputSelect",
  INPUT_GPS_BUTTON = "inputGPSButton",
  INPUT_MULTIPLE_CREATE = "inputMultipleCreate",
  INPUT_IMAGE = "inputImage",
  INPUT_METAEXPRESSION = "inputMetaExpression",
  INPUT_MULTIPLE_SELECT = "inputMultipleSelect",
  INPUT_MATRIX_EDITOR = "inputMatrixEditor",
  ITEM_GALLERY = "itemGallery",
  INPUT_VISIBLE_BY_PLAN = "inputVisibleByPlan",
  JSON_EDITOR = "jsonEditor",
  SQL_QUERY_EDITOR = "sqlQueryEditor",
  SELECT_ENV_VARIABLE = "selectEnvVariable",
}

export enum LANG_ACTION_NOTIFICATIONS {
  CREATE = "create",
  EDIT = "edit",
  DELETE = "delete",
  ARCHIVE = "archive",
  RESTORE = "restore",
  DOWNLOAD = "download",
  RELOAD = "reload",
  HELP = "help",
  FETCH = "fetch",
  LICENSE = "license",
  UNLICENSE = "unlicense",
  UPLOAD = "upload",
  OTHER_ACTION = "otherAction",
  RESETTING_PASSWORD = "resettingPassword",
}

export enum LANG_OTHER_UI_ACTIONS {
  DOWNLOAD_RAW = "downloadRaw",
  DOWNLOAD_PDF = "downloadPDF",
  SINGLE_GENERIC_ACTION = "singleGenericAction",
  BULK_GENERIC_ACTION = "bulkGenericAction",
  BULK_GENERIC_ACTION_FROM_CSV = "bulkGenericActionFromCSV",
  RUN_IN_BACKGROUND_MESSAGE = "runInBackgroundMessage",
  HIDE_ARCHIVE = "hideArchive",
  DISPLAY_ARCHIVE = "displayArchive",
  SHOW_LESS = "showLess",
  SHOW_MORE = "showMore",
}

export type TOperator = CONDITION_OPERATOR | OPERATION_OPERATOR;

type TlangBaseGenericTerms = {
  [key in LANG_GENERIC_TERMS]: string;
};

export type TLangGenericOperators = {
  operators: {
    [key in TOperator | NUMBER_FILTER]: string;
  };
};

type TLangGenericContent = {
  content: {
    nothing: string;
    templateField: string;
  };
};

type TPeriod = {
  day: string;
  hour: string;
  min: string;
};
type TTime = {
  period: TPeriod;
  periodShort: TPeriod;
  periods: TPeriod;
  periodsShort: TPeriod;
};

type TQueryMessage = {
  query: {
    deactivated: {
      title: string;
      description: string;
    };
    verified: {
      title: string;
      description: string;
    };
    verify: {
      title: string;
      description: string;
    };
  };
};
type TLangGenericTerms = TlangBaseGenericTerms &
  TLangGenericOperators &
  TLangGenericContent &
  TTime &
  TQueryMessage;

type TLangPeriods = {
  day: string;
  week: string;
  month: string;
  year: string;
  days: string;
  weeks: string;
  months: string;
  years: string;
  daily: string;
  weekly: string;
  monthly: string;
  yearly: string;
  date: string;
};

type TLangActions = {
  [key in LANG_ACTIONS]: string;
};

// Empty table types
type TDefaultEmptyTable = {
  title: string;
};
type TBaseEmptyTable = {
  title: string;
  description: string;
};
type TSearchFilterEmptyTable = {
  searchFilter: {
    title: string;
    search: {
      description: string;
    };
    filter: {
      clearButton: any;
      description: string;
    };
    filtersYouHaveSet: string;
  };
};
type TEmptySecretKeys = {
  secretsKeys: TBaseEmptyTable;
};

export enum EMPTY_ENTITY_TABLE {
  USERS = "users",
  TEAMS = "teams",
  LISTS = "lists",
  ORDERS = "orders",
  STOCKS = "stocks",
  DISCOUNTS = "discounts",
  STOCK_MOVEMENTS = "stockMovements",
  DOCUMENTS = "documents",
  ACTIVITIES = "activities",
  DASHBOARDS = "dashboards",
  CUSTOMERS = "customers",
  WORKFLOWS = "workflows",
  GENERIC_ENTITY = "genericEntity",
}

type WithSubEntities<K extends string, T> = K extends
  EMPTY_ENTITY_TABLE.LISTS | EMPTY_ENTITY_TABLE.WORKFLOWS
  ? T & { [x: string]: TBaseEmptyTable }
  : T;

type TEmptyEntityTable = {
  [key in EMPTY_ENTITY_TABLE]: WithSubEntities<key, TBaseEmptyTable>;
};

type TEmptyEntityTableLang = TDefaultEmptyTable &
  TSearchFilterEmptyTable &
  TEmptySecretKeys &
  TEmptyEntityTable;

type TLangComponents = {
  inputImage: {
    remove: string;
    add: string;
    addURL: string;
    noImageSelected: string;
    fileTypeNotSupported: string;
  };
  inputErrors: { [key in ERROR_LIST]: string };
  filters: any;
  columns: any;
  jsonEditor: any;
  metaExpressions: {
    tooltip: string;
    scope: { [key in META_EXPRESSION_SCOPE]: string };
  };
  inputSelect: {
    noOptions: string;
    scope: {
      [key in LIST_SCOPE]: string;
    };
    boolean: {
      true: string;
      false: string;
    };
    clear: string;
    folderType: { [key in FOLDER_TYPE]: string };
  };
  inputGPSButton: TInputAttributeLang;
  operators: any;
  maps: any;
  search: string;
  table: any;
  expansionPanel: any;
  inputMetaExpression: TInputAttributeLang;
  sqlQueryEditor: {
    formatSql: string;
    insertMetaExpression: string;
    placeholder: string;
    removeMetaExpression: string;
    removeQuery: string;
  };
  updateMonitor: TInputAttributeLang;
  inputCondition: TInputAttributeLang;
  inputOperation: TInputAttributeLang;
  inputMultipleCreate: {
    options: {
      addNew: string;
    };
  };
  inputMultipleSelect: {
    limitWarningModal: TWarningModalLang;
    noValuesToDisplay: string;
    showingSelectedValues: string;
    showPrevious: string;
    showMoreWithCount: string;
    truncatedUseSearchHint: string;
    truncatedViewTooltip: string;
  };
  card: any;
  errors: any;
  emptyTable: TEmptyEntityTableLang;
  inputMatrixEditor: TInputAttributeLang;
  itemGallery: TInputAttributeLang;
  inputVisibleByPlan: TCreateEditModalLang;
  selectEnvVariable: {
    buttonLabel: string;
    tooltip: string;
    noVariables: string;
  };
  dialog: {
    bulkAddOptionsFromCSV: any;
  };
};

export type TLangMcpTokens = {
  menuLabel: string;
  title: string;
  description: string;
  copyTokenButton: string;
  copyTokenWarning: string;
  inputLabel: {
    title: string;
    placeholder: string;
  };
  inputScope: {
    title: string;
    options: {
      read: string;
      read_write: string;
    };
  };
  inputExpiresInDays: {
    title: string;
  };
  createTokenButton: string;
  columns: {
    label: string;
    scope: string;
    created: string;
    lastUsed: string;
    expires: string;
    status: string;
  };
  status: {
    active: string;
    revoked: string;
  };
  revokeButton: string;
  noTokens: string;
  closeButton: string;
  errors: {
    loadTokens: string;
    createToken: string;
    revokeToken: string;
  };
};

type TLangMainLayout = {
  selectedClient: string;
  selectedLang: string;
  defaultLang: string;
  frenchLanguage: string;
  englishLanguage: string;
  espagnolLanguage: string;
  portugueseLanguage: string;
  vietnameseLanguage: string;
  navbar: {
    menu: {
      organisation: string;
      switchClient: string;
      changeClient: string;
      modal: {
        searchClient: string;
        changeClientButton: string;
      };
    };
  };
  sidebar: {
    summary: string;
    home: string;
    users: string;
    mobileUsers: string;
    webUsers: string;
    dashboards: string;
    clients: string;
    list: string;
    lists: string;
    customers: string;
    map: string;
    pictures: string;
    documents: string;
    workflows: string;
    workflowBuilder: string;
    activities: string;
    jobs: string;
    triggers: string;
    panorama: string;
    erp: string;
    orders: string;
    stockMovements: string;
    discountsOld: string;
    detectPictures: string;
    optimetriks: string;
    panoramaPages: {
      customersNew: string;
      customers: string;
      customerGroups: string;
      balances: string;
      products: string;
      productGroups: string;
      pricesNew: string;
      prices: string;
      discounts: string;
      orderLines: string;
      stockNew: string;
      stock: string;
      recommendations: string;
      distributors: string;
      warehouses: string;
    };
    erpLists: string;
    webhooks: string;
    teams: string;
    database: string;
    environment: string;
    backgroundOperations: string;
    detect: string;
    catalogue: string;
  };
  noAccessiblePage: {
    title: string;
    description: string;
  };
  unreachableLandingPage: {
    title: string;
    description: string;
  };
  mcpTokens: TLangMcpTokens;
  // buttons
  logOutButton: string;
  forceSyncButton: string;
  documentationButton: string;
  giveFeedbackButton: string;
  goToFPDownloadButton: string;
  lastUpdate: string;
  dataLastUpdated: string;
  replicaLagWarning: string;
  goToHelpCenterButton: string;
  goToContactSupportButton: string;
  goToViewSupportRequestsButton: string;
  editProfileButton: string;
  whatsNewAtFieldProButton: string;
  changePasswordButton: string;
  goToStripePortalButton: string;
  // modals
  editProfileModal: any;
  changePasswordModal: any;
  banner: {
    title: string;
  };
};

type TLangInProgressNotifications = {
  [key in LANG_ACTION_NOTIFICATIONS]: string;
};

type TLangSuccessNotifications = {
  default: string;
  defaultPlural: string;
  downloadList: string;
  downloadChart: string;
  copyInClipboardSuccess: string;
};

type TLangErrorNotifications = {
  default: string;
  internalError: string;
  validationError: string;
  networkError: string;
  requestUnreachable: string;
  freeTrialReachedError: string;
  copyInClipboardError: string;
  timeoutError: string;
};

type TLangNotifications = {
  inProgressNotifications: TLangInProgressNotifications;
  successNotifications: TLangSuccessNotifications;
  errorNotifications: TLangErrorNotifications;
};

type TLangModal = {
  confirm: string;
  cancel: string;
  save: string;
  add: string;
  test: string;
  create: string;
  delete: string;
  archive: string;
  edit: string;
  restore: string;
  proceed: string;
  upload: string;
  apply: string;
  select: string;
  recover: string;
  selectAll: string;
  deselectAll: string;
  validationText: string;
  dataLossMessage: string;
  confirmationMessage: string;
  downloadWIP: string;
  downloadCSV: {
    title: string;
    tooltip: string;
  };
  uploadWIP: string;
  loadWIPModalTitle: string;
  loadWIPModalDescription: string;
  warningTitle: string;
  createEditOption: {
    createTitle: string;
    editTitle: string;
    inputKey: TInputAttributeLang;
    inputLabel: TInputAttributeLang;
    inputIgnorePropagation: TInputAttributeLang;
  };
  confirmCloseModal: {
    title: string;
    description: string;
  };
  lockModal: TLockModalLang;
  uploadCSVMessage: {
    message: string;
    errorMessage: string;
  };
  archiveDeleteMultiple: {
    title: string;
    description: string;
  };
  archiveDelete: {
    title: string;
    description: string;
  };
  archiveMessage: string;
  deleteMessage: string;
  bulk: {
    warningOnConfirm: {
      description: string;
      title: string;
    };
    mainModal: {
      title: string;
      description: string;
    };
    importErrors: {
      [key in string]: string;
    };
  };
  downloadConfirm: string;
  tabs: {
    settings: string;
    overview: string;
    conditions: string;
    all: string;
    content: string;
    recipients: string;
    parameters: string;
    step_settings: string;
  };
  changeTag: {
    title: string;
    [inputName: string]: string;
  };
};

type TLangLoginScreen = {
  passwordLostModal: TCreateEditModalLang;
  passwordLostMessageModal: TCreateEditModalLang;
  passwordLostButton: string;
  inputEmail: TInputAttributeLang;
  inputPassword: TInputAttributeLang;
  googleLoginButton: string;
  emailPasswordLoginButton: string;
  orSeparator: string;
  titleLogin: string;
  subTitleLogin1: string;
  subTitleLogin2: string;
  error: {
    wrongInformationProvided: string;
    errorInPasswordUpdating: string;
  };
} & {
  [inputAttName: string]: TInputAttributeLang;
};

/**
 * Web access rights editor. The page / section / capability maps are keyed by
 * the catalogue ids (webAccessCatalog.ts); a test asserts every id is covered
 * in every locale, which types alone would not do.
 */
export type TWebAccessProfileLang = {
  breadcrumb: string;
  canAccess: string;
  addPage: string;
  searchPlaceholder: string;
  pagesGroup: string;
  capabilitiesGroup: string;
  removePage: string;
  viewLocked: string;
  personaDescription: string;
  landingPage: {
    title: string;
    description: string;
    none: string;
    invalid: string;
    clear: string;
  };
  newProfile: string;
  resetPersonaDefaults: string;
  emptyState: string;
  noSearchMatch: string;
  noPageWarning: string;
  undo: string;
  redo: string;
  discardChanges: string;
  confirmLeave: TConfirmModalLang;
  dataScope: {
    title: string;
    teamBased: string;
    teamBasedDescription: string;
    allData: string;
    allDataDescription: string;
  };
  actions: Record<string, string>;
  sections: Record<string, string>;
  pages: Record<string, string>;
  capabilities: Record<string, string>;
  capabilityDescriptions: Record<string, string>;
  /** Keyed by CAPABILITY_LEVEL. */
  capabilityLevels: Record<string, string>;
  capabilitiesCard: {
    title: string;
    help: string;
    adminOnly: string;
    /** Label of the only chip of a single-code capability. */
    on: string;
  };
  /** Keyed by action code, for the codes that carry no ACTION_TYPE prefix. */
  extraActions: Record<string, string>;
  scopedActions: {
    /** All three keyed by page id, the questions then by ACTION_TYPE. */
    titles: Record<string, string>;
    helps: Record<string, string>;
    questions: Record<string, Record<string, string>>;
    limited: Record<string, string>;
    /** Keyed by page id, "{{0}}" being the action label to turn on. */
    notGrantedHints: Record<string, string>;
    selectToInclude: Record<string, string>;
    selectToExclude: Record<string, string>;
    /** Both keyed by page id, then by ACTION_INCLUDE_TYPE. */
    includeTypes: Record<string, Record<string, string>>;
    summary: Record<string, Record<string, string>>;
    picker: {
      /** Keyed by page id; the search placeholder carries the option count. */
      searchPlaceholder: Record<string, string>;
      allGroup: Record<string, string>;
      noMatch: Record<string, string>;
      /** Keyed by page id: the client holds no object to pick from. */
      noEntities: Record<string, string>;
      /** "{{0}}" being a count. */
      selectedGroup: string;
      moreChips: string;
      excludedCount: string;
      includedCount: string;
      emptySelection: string;
      clear: string;
      /** Labels of the toggle folding the objects away. */
      expand: string;
      collapse: string;
    };
  };
  /** The panel of the Lists row: scope, record level, structure rights. */
  lists: {
    whichLists: string;
    includeTypes: Record<string, string>;
    selectListsToInclude: string;
    selectListsToExclude: string;
    /** Keyed by ACTION_INCLUDE_TYPE: "All lists", "Except {{0}}", "Only {{0}}". */
    summary: Record<string, string>;
    whatCanTheyDo: string;
    recordLevels: Record<string, string>;
    recordLevelHelp: string;
    manageStructure: string;
    manageStructureHelp: string;
    /** "{{0}}" being how many structure rights are granted. */
    structureSummary: string;
  };
};

export type TMobileAccessProfileLang = {
  breadcrumb: string;
  /** Tooltip of the locked View chip: removing the row is how View goes. */
  viewLocked: string;
  addRow: string;
  searchPlaceholder: string;
  /** Shown by the picker once every row is on the profile. */
  allRowsAdded: string;
  /** Keyed by the check an action can enforce (ADDITIONNAL_CHECKING). */
  subActions: Record<
    string,
    { enforced: string; optional: string; tooltip: string }
  >;
  /** "{{0}}/{{1}}": how many rows of the section the role can view. */
  sectionCount: string;
  /** Keyed by MOBILE_ACCESS_SECTION. */
  sections: Record<string, string>;
  /** Keyed by row id. */
  rows: Record<string, string>;
  /** Keyed by action code, for the codes the action type does not name. */
  actions: Record<string, string>;
};

export type TLangDefaultScreen = {
  subject: string;
  subjects: string;
  chooseClient: string;
  actions: TCustomDirectActionLabel;
  columns: any;
  createEditModal?: TCreateEditModalLang;
  createMenu: any;
  editMenu: any;
  archiveModal?: TArchiveDeleteModalLang & { warningText: string };
  deleteModal?: TArchiveDeleteModalLang;
  replicateModal?: TCreateEditModalLang & { replicateTitle: string };
  customNotifications: any;
};

type TPLANG_ID = PLAN_ID | "entreprise";

type TLangPlans = {
  [key in TPLANG_ID]: {
    name: string;
    description: string;
    priceLabel: string;
    currentPlan: string;
    subscribeButton: string;
    featuresTitle: string;
    features: {
      [key: string]: string;
    };
  };
};

type TLangPacks = {
  [key in PACK_ID]: {
    features: {
      [key: string]: string;
    };
  };
};

export type TRawExports = {
  activity: {
    mainSheet: string;
    matrixSheet: string;
    schemaSheet: string;
  };
  list: {
    itemSheet: string;
    schemaSheet: string;
  };
  pdf: {
    [key: string]: string;
  };
};

export type TLifecycleStatusUsageTableLang = {
  id: string;
  name: string;
  more: string;
  idLabels: Record<string, string>;
};

export type TLang = {
  genericTerms: TLangGenericTerms;
  periods: TLangPeriods;
  lifecycleStatusUsageTable: TLifecycleStatusUsageTableLang;
  rawExports: TRawExports;
  actions: {
    [key in LANG_OTHER_UI_ACTIONS]: string;
  } & TLangActions;
  components: TLangComponents;
  mainLayout: TLangMainLayout;
  notifications: TLangNotifications;
  modal: TLangModal;
  containers: {
    detect: {
      containers: {
        detect: {
          subCategories: {
            catalogue: TCreateEditModalLang;
            dashboard: TCreateEditModalLang;
          };
        };
      };
    };
    calendar: {
      subCategories: {
        calendar: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          events: TLangDefaultScreen;
          swapEventsButton: {
            tooltip: string;
          };
          transferEventsButton: {
            tooltip: string;
          };
          moveEventsButton: {
            tooltip: string;
          };
        };
        calendarEvent: TLangDefaultScreen;
      };
    };
    triggers: {
      subCategories: {
        triggers: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
      };
    };

    login: {
      subCategories: {
        login: TLangLoginScreen;
      };
    };
    clients: {
      subCategories: {
        clients: TLangDefaultScreen & {
          webAccessProfile: TWebAccessProfileLang;
          mobileAccessProfile: TMobileAccessProfileLang;
          createEditModal: TCreateEditModalLang;
          deleteModal: TArchiveDeleteModalLang;
          setClientLiveModal: TConfirmModalLang;
          clientForm: TClientFormLang;
          // Rich nested structure used by the lifecycle visual + JSON
          // editor (modules list, configure-module canvas, status modal,
          // permission modal, transition edge badges).
          lifecycle: TLifecycleLang;
          inputWorkspaceType: {
            title: string;
            tooltip: string;
          };
          inputParentTemplate: {
            title: string;
            tooltip: string;
          };
          workspaceTypeOptions: {
            CUSTOM: string;
            TEMPLATE: string;
            CHILD: string;
          };
        };
        folders: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          deleteModal: TArchiveDeleteModalLang;
        };
      };
    };
    users: {
      subCategories: {
        mobileUsers: TLangDefaultScreen & {
          deleteModal: TArchiveDeleteModalLang;
          userAlreadyInTeamModal: any;
          maxLicencesModal: any;
          createEditModal: TCreateEditModalLang;
          createMenu: any;
        };
        webUsers: TLangDefaultScreen & {
          deleteModal: TArchiveDeleteModalLang;
          createEditModal: TCreateEditModalLang;
          bulkCreateModal: TCreateEditModalLang;
          createMenu: any;
        };
      };
      deleteModal: TArchiveDeleteModalLang;
    };
    orders: {
      subCategories: {
        orders: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          editItemModal: {
            title: string;
            availableStock: string;
            availableStockUnit: string;
            selectFormat: string;
            saveChanges: string;
            exceedsAvailable: string;
            unitPrice: string;
            unitPriceMissing: string;
          };
          bulkPrintReceipts: {
            title: string;
            description: string;
            actionLabel: string;
            formatNote: string;
            confirmButton: string;
            uploadFile: {
              description: string;
              linkLabel: string;
              title: string;
            };
            errors: {
              emptyFile: string;
              noIdColumn: string;
              invalidFile: string;
            };
            successMessage: string;
            backgroundMessage: string;
            uploadingText: string;
            willRunInBackground: string;
            ordersFound: string;
            printing: string;
          };
        };
        stock: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        discounts: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        prices: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        stockMovements: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          modes: Record<STOCK_MOVEMENT_MODE, string>;
          receiveStock: {
            receiveButton: string;
            addItems: string;
            title: string;
            intro: string;
            selectStorage: string;
            addSkusIntro: string;
            selectSku: string;
            cancel: string;
            submit: string;
            noSkusAdded: string;
            clickToAdd: string;
            skuName: string;
            currentStock: string;
            format: string;
            quantity: string;
            newStock: string;
            units: string;
            successMessage: string;
            pendingNotice: {
              prefix: string;
              highlight: string;
              middle: string;
              status: string;
              suffix: string;
            };
            noStorageAvailable: {
              title: string;
              description: string;
            };
            errors: {
              quantityMustBePositive: string;
              skuAlreadySelected: string;
              submitNoSkus: string;
            };
            bulkUpload: {
              menuItem: string;
              title: string;
              description: string;
              linkLabel: string;
              confirmButton: string;
              fileUploadLabel: string;
              uploadingText: string;
              templateFileName: string;
              successMessage: string;
              errorReport: {
                summary: string;
                title: string;
                row: string;
              };
            };
          };
          transferStock: {
            title: string;
            intro: string;
            selectStorage: string;
            source: string;
            destination: string;
            addSkusIntro: string;
            selectSku: string;
            noSkusAdded: string;
            clickToAdd: string;
            submit: string;
            cancel: string;
            transferButton: string;
            addItems: string;
            csvUpload: string;
            available: string;
            quantity: string;
            successMessage: string;
            deleteSuccess: string;
            deleteConfirmTitle: string;
            deleteConfirmDescription: string;
            noStorageAvailable: {
              title: string;
              description: string;
            };
            errors: {
              sameSourceDestination: string;
              insufficientQuantity: string;
              quantityMustBePositive: string;
              skuAlreadySelected: string;
              submitNoSkus: string;
            };
            pendingNotice: {
              prefix: string;
              highlight: string;
              middle: string;
              status: string;
              suffix: string;
            };
            bulkUpload: {
              menuItem: string;
              title: string;
              description: string;
              linkLabel: string;
              confirmButton: string;
              fileUploadLabel: string;
              uploadingText: string;
              templateFileName: string;
              successMessage: string;
              errorReport: {
                summary: string;
                title: string;
                row: string;
              };
            };
          };
          editItemModal: {
            title: string;
            availableStock: string;
            availableStockUnit: string;
            selectFormat: string;
            saveChanges: string;
            exceedsAvailable: string;
            quantityMustBePositive: string;
            unitPrice: string;
            unitPriceMissing: string;
          };
          deleteItemModal: {
            title: string;
            description: string;
            confirm: string;
          };
        };
        oldStock: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        credit: {
          subject: string;
          creditLimit: string;
          availableCredit: string;
          outstandingBalance: string;
          overdueAmount: string;
          creditTerm: string;
          unableToLoadCreditInfo: string;
          invalidCustomerId: string;
          transactionHistory: string;
          transactionManagement: string;
          credit: string;
          noValidCustomerIds: string;
        };
        invoice: {
          subject: string;
        };
        creditnote: {
          subject: string;
        };
      };
    };
    teams: {
      subCategories: {
        teams: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          bulkCreateModal: TCreateEditModalLang;
          manageHierarchyModal: TCreateEditModalLang;
          createMenu: any;
        };
        groups: TLangDefaultScreen & {
          deleteGroupModal: TArchiveDeleteModalLang;
          archiveGroupModal: TArchiveDeleteModalLang;
          deleteGroupsModal: TArchiveDeleteModalLang;
          archiveGroupsModal: TArchiveDeleteModalLang;
        };
      };
    };
    lists: {
      subCategories: {
        lists: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          moreActions: {
            title: string;
          };
          archiveModal: TArchiveDeleteModalLang;
          warningModal: any;
          bulkAddOptionsFromCSV: any;
        };
        items: TLangDefaultScreen & {
          duplicatesChipLabel: string;
          createEditModal: TCreateEditModalLang & {
            createTitleShort: string;
          };
          deleteModal: TArchiveDeleteModalLang;
          bulkEditModal: any;
          bulkDownload: {
            column: [name: string];
          };
          buyXGetY: TCreateEditModalLang;
          discounts: TCreateEditModalLang;
          customerPromoGroups: {
            conditionInput: TInputAttributeLang;
          };
          createEditFindDuplicateModal: TCreateEditModalLang;
          detailDialog: {
            emptyPictures: string;
            emptyNotes: string;
            insights: string;
            qrCode: string;
            notes: string;
            validation: string;
            emptyActivityReportsPictures: string;
            activityReportsPicturesTitle: string;
            loadingActivityReportsPictures: string;
            loyaltyBalance: string;
            orderSummary: string;
            transactionManagement: string;
            noCreditPolicy: string;
          };
        };
        territories: TLangDefaultScreen & {
          displayTerritories: string;
          displayCustomers: string;
          satelliteView: string;
          totalGeoTaggedCustomersCount: string;
          totalTerritoriesCount: string;
          bulkCreateModal: TCreateEditModalLang & {
            actionsDescription: {
              single: string;
              plural: string;
            };
          };
          createTerritoryModal: TCreateEditModalLang;
          deleteTerritoriesModal: TArchiveDeleteModalLang;
          deleteTerritoryModal: TArchiveDeleteModalLang;
          editTerritoryModal: TCreateEditModalLang;
          inputCreateShape: {
            title: string;
            menuOptions: {
              draw: string;
            };
            drawMode: {
              description: string;
            };
          };
          inputEditShape: {
            editMode: {
              title: string;
              editButton: string;
              resetButton: string;
            };
            drawMode: {
              title: string;
              description: string;
            };
          };
        };
        map: TLangDefaultScreen;
      };
    };
    dashboards: {
      subCategories: {
        dashboards: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          defaultDashboards: {
            [key in DEFAULT_DASHBOARD]: string;
          } & {
            [key: string]: any;
          };
          statusType: {
            [key in DASHBOARD_STATUS_TYPE]: string;
          };
          timeoutChart: {
            title: string;
            downloadable: string;
            notDownloadable: string;
            downloadMessage: { title: string; description: string };
          };
          chartDisabled: {
            title: string;
            description: string;
          };
          aggregator: {
            [key: string]: string;
          };
        };
      };
    };
    environment: {
      subCategories: {
        tableOptimizations: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        databaseSchema: TLangDefaultScreen & {
          detailTableView: string;
          simpleTableView: string;
          direction: string;
        };
        sections: {
          [key: string]: { title: string; description: string };
        };
      };
    };
    workflows: {
      subCategories: {
        workflows: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang & {
            searchTitle: string;
            status: TInputAttributeLang & {
              lastReport: string;
              current: string;
              since: string;
            };
          };
          createEditTagModal: TCreateEditModalLang;
          steps: {
            subjects: string;
            subject: string;
          };

          stepConfigurationDialog: TCreateEditModalLang /* {
            scope: string;
            parameters: string;
            condition: string;
            assignmentRules: string;
            delete: string;
            save: string;
          };*/;
          active: string;
          archived: string;
        };
        webhooks: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          schema: TWebhookScreenLang;
        };
        activityReports: TLangDefaultScreen & {
          detailDialog: {
            details: string;
            pictures: string;
            emptyPictures: string;
            empty: string;
            showAllPictures: string;
            multiPictureReadOnlyHint: string;
            editDisabledTooltip: string;
            disabledIconTooltip: {
              [key: string]: string;
            };
          };
          sharePdfButton: {
            loadingMessage: string;
            tooltip: string;
            modal: {
              description: string;
            };
          };
          copyLinkButton: {
            tooltip: string;
            confirmationMessage: string;
          };
          assignToMe: {
            title: string;
            buttonTitle: string;
            columns: {
              [item: string]: string;
            };
          };
        };

        workflowReports: TLangDefaultScreen;
        jobHistory: TLangDefaultScreen;
        activities: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          createEditTagModal: TCreateEditModalLang;
          active: string;
          archived: string;
          questions: TActivityScreenLang;
        };
        metaExpressions: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
      };
    };

    jobs: {
      subCategories: {
        backgroundOperations: {
          subject: string;
          subjects: string;
          cancelSuccess: string;
          actions: {
            cancel: string;
            confirmCancel: string;
            autoRefresh: string;
          };
          cancelModal: TConfirmModalLang & {
            descriptionWithWorkflow: string;
          };
          columns: {
            client: string;
            operationType: string;
            workflow: string;
            workflowReport: string;
            workflowStep: string;
            workflowStatus: string;
            status: string;
            triggeredBy: string;
            startedAt: string;
            duration: string;
            errorMessage: string;
          };
        };
        jobs: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        transformations: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
          initJobModal: TCreateEditModalLang;
        };
        notifications: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
        };
        http_requests: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
        };
        integrations: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        timer: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
        };
        streams: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
          useStreamModal: { title: string; submitButton: string };
        };
        scripts: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          runJobModal: TRunJobModalLang;
        };
        prediction: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        updateQueryAttributes: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        createItemJob: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
        editItemJob: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
        };
      };
    };
    documents: {
      subCategories: {
        documents: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          deleteModal: TArchiveDeleteModalLang;
          scope: {
            [key in DOCUMENT_SCOPE]: string;
          };
        };
      };
    };
    pictures: {
      subCategories: {
        pictures: TLangDefaultScreen & {
          createEditModal: TCreateEditModalLang;
          galleryModal: any;
          downloadModal: any;
        };
      };
    };

    home: {
      title: {
        translation: string;
      };
      overview: {
        translation: string;
      };
      subtitle: {
        translation: string;
      };
      frequentlyViewedDashboards: {
        title: {
          translation: string;
        };
      };
      homeDashboard: {
        title: {
          translation: string;
        };
      };
      noAccessibleDashboards: {
        title: string;
        description: string;
        seeDashboards: string;
      };
      containers: {
        home: TCreateEditModalLang;
      };
    };
  };
  plans: TLangPlans;
};

type TLifecycleEntityMap = { [E in LIFE_CYCLE_ENTITY]: string };

type TLifecycleModulesListLang = {
  configureModule: string;
  modulesConfigured: string;
  noModuleConfigured: string;
  clickToStartHint: string;
  viewConfiguration: string;
  selectModule: string;
  allModulesConfigured: string;
};

type TLifecyclePickListModalLang = {
  title: string;
  description: string;
  searchPlaceholder: string;
  emptyHint: string;
  noSearchMatch: string;
  configuredSectionTitle: string;
  notConfiguredSectionTitle: string;
  configuredBadge: string;
  notConfiguredBadge: string;
};

type TLifecycleConfigureModuleLang = {
  titles: TLifecycleEntityMap;
  breadcrumbAddon: string;
  breadcrumbCurrent: string;
  manageStatus: string;
  manageStatusDescription: string;
  createStatus: string;
  searchStatus: string;
  noStatusYet: string;
  noStatusMatch: string;
  alreadyOnCanvas: string;
  dragToCanvas: string;
  addToCanvas: string;
  edit: string;
  editLabelAndColor: string;
  delete: string;
  systemCannotDelete: string;
  saveButton: string;
  cancelButton: string;
  unlinkedTransitions: string;
  unlinkedCount: string;
  relinkTooltip: string;
  placeholderHint: string;
  orphanWarning: {
    title: string;
    body: string;
    footer: string;
    back: string;
    skip: string;
  };
  importFromListButton: string;
  importDialog: {
    title: string;
    description: string;
    sourceLabel: string;
    sourcePlaceholder: string;
    cancel: string;
    confirm: string;
    noListsHint: string;
  };
  conflictBanner: {
    title: string;
    description: string;
    duplicateRole: string;
  };
};

type TLifecycleRolesPickerLang = {
  optimetriksTeamLabel: string;
  optimetriksTeamTooltip: string;
  webRole: string;
  mobileRole: string;
  selectPlaceholder: string;
  atLeastOneRoleError: string;
};

type TLifecycleTransitionEdgeLang = {
  settingsTooltip: string;
  stockIncreaseLabel: string;
  stockIncreaseTooltip: string;
  stockDecreaseLabel: string;
  stockDecreaseTooltip: string;
  legacyLabel: string;
  legacyTooltip: string;
  deliveryLabel: string;
  deliveryTooltip: string;
  menuUnlink: string;
  menuViewPermissions: string;
  variantsTooltip: string;
};

type TLifecycleStatusModalLang = {
  createTitle: string;
  editTitle: string;
  statusName: string;
  statusNamePlaceholder: string;
  backgroundColor: string;
  textColor: string;
  colorSection: string;
  generateBgFromText: string;
  generateBgFromTextAria: string;
  generateTextFromBg: string;
  generateTextFromBgAria: string;
  statusId: string;
  statusIdPlaceholder: string;
  idAlreadyUsed: string;
  idTooltipSystem: string;
  idTooltipLocked: string;
  idTooltipUnlocked: string;
  idTooltipCreate: string;
  systemLockTooltip: string;
  editIdTooltip: string;
  confirmEditIdTitle: string;
  confirmEditIdBody1: string;
  confirmEditIdBody2: string;
  confirmEditIdConfirm: string;
  creationRulesSection: string;
  creationRulesDescription: string;
  creationRulesEmpty: string;
  addRule: string;
  editRule: string;
  removeRule: string;
  expandRule: string;
  collapseRule: string;
  collapsedTitlePrefix: string;
  collapsedScopeDefault: string;
  collapsedScopeRoles: string;
  collapsedSeparator: string;
  ruleSummaryDefault: string;
  ruleSummaryRoles: string;
  ruleSummaryAutomationNone: string;
  ruleSummaryDeliveryWithStatus: string;
  ruleSummaryDeliveryDefaultStatus: string;
  ruleSummaryStockDecrease: string;
  ruleSummaryStockIncrease: string;
  ruleSummaryStockUpdate: string;
  ruleSummaryAndSeparator: string;
  orderTypeAny: string;
  orderTypePreSale: string;
  orderTypeDirectSale: string;
  editPermissionsSection: string;
  editPermissionsDescription: string;
  restrictEdit: string;
  restrictEditTooltip: string;
  rolesAllowedToEdit: string;
};

type TLifecycleRuleEditorLang = {
  createTitle: string;
  editTitle: string;
  scopeSection: string;
  scopeDescription: string;
  orderType: string;
  orderTypeTooltip: string;
  orderTypeAny: string;
  orderTypePreSale: string;
  orderTypeDirectSale: string;
  mode: string;
  modeTooltip: string;
  modeAny: string;
  modeRequest: string;
  modeReturn: string;
  modeTransfer: string;
  modeReceive: string;
  setAsDefault: string;
  setAsDefaultTooltip: string;
  scopeToRoles: string;
  scopeToRolesTooltip: string;
  selectRoleCardTitle: string;
  duplicateRoleError: string;
  automationSection: string;
  automationDescription: string;
  createOrderDelivery: string;
  createOrderDeliveryTooltip: string;
  deliveryDays: string;
  deliveryDaysTooltip: string;
  deliveryDaysPlaceholder: string;
  deliveryStatus: string;
  deliveryStatusTooltip: string;
  deliveryStatusNoneConfigured: string;
  deliveryStatusUseDefault: string;
  updateStock: string;
  updateStockTooltip: string;
  stockTask: string;
  stockTaskTooltip: string;
};

type TLifecyclePermissionModalLang = {
  title: string;
  selectRoleCardTitle: string;
  configurationCardTitle: string;
  addConfiguration: string;
  removeConfiguration: string;
  allOrderTypesConfigured: string;
  orderType: string;
  orderTypeTooltip: string;
  orderTypeAny: string;
  orderTypePreSale: string;
  orderTypeDirectSale: string;
  orderTypeDuplicateError: string;
  automationSection: string;
  automationDescription: string;
  createOrderDelivery: string;
  createOrderDeliveryTooltip: string;
  deliveryDays: string;
  deliveryDaysTooltip: string;
  deliveryDaysPlaceholder: string;
  deliveryStatus: string;
  deliveryStatusTooltip: string;
  deliveryStatusNoneConfigured: string;
  deliveryStatusUseDefault: string;
  updateStock: string;
  updateStockTooltip: string;
  stockTask: string;
  stockTaskTooltip: string;
};

type TLifecycleStockTaskOptionsLang = {
  decreaseLabel: string;
  increaseLabel: string;
  updateStockLabel: string;
};

type TLifecycleTasksPickerLang = {
  title: string;
  noTask: string;
  deprecatedTooltip: string;
  pickTaskPlaceholder: string;
  deprecatedSuffix: string;
  addButton: string;
};

export type TLifecycleLang = {
  modulesTitle: string;
  modulesDescription: string;
  jsonMode: string;
  visualMode: string;
  formatJsonButton: string;
  formatJsonSuccess: string;
  formatJsonError: string;
  modulesList: TLifecycleModulesListLang;
  pickListModal: TLifecyclePickListModalLang;
  entities: TLifecycleEntityMap;
  entityWords: TLifecycleEntityMap;
  configureModule: TLifecycleConfigureModuleLang;
  rolesPicker: TLifecycleRolesPickerLang;
  transitionEdge: TLifecycleTransitionEdgeLang;
  statusModal: TLifecycleStatusModalLang;
  ruleEditor: TLifecycleRuleEditorLang;
  permissionModal: TLifecyclePermissionModalLang;
  stockTaskOptions: TLifecycleStockTaskOptionsLang;
  tasksPicker: TLifecycleTasksPickerLang;
};

type TClientFormLang = {
  automatedBillingTab: {
    planOverview: {
      title: string;
      description: string;
      total: string;
      subTotal: string;
      inputLicensePlan: {
        title: string;
        noPlan: string;
        subscribe: string;
        change: string;
        perLicensePer: string;
      };
      inputNumberOfLicenses: {
        title: string;
        button: string;
        licence: string;
        licences: string;
      };
      inputNumberOfLicensesModal: {
        title: string;
        confirm: string;
        youCurrentlyHave: string;
        inputLabel: string;
        inputSubText: string;
      };
      projectedTotal: {
        title: string;
      };
      inputBillingCycle: {
        title: string;
        switchTo: string;
        switchToMonthly: string;
        switchToYearly: string;
        perLicensePer: string;
      };
      inputBillingCycleModal: {
        title: string;
        inputTitle: string;
        saveXPercent: string;
        perLicensePer: string;
        confirm: string;
      };
      inputWorkspaceType: {
        title: string;
        tooltip: string;
      };
      inputParentTemplate: {
        title: string;
        tooltip: string;
      };
      inputSupportPack: {
        title: string;
        add: string;
        change: string;
      };
      inputSupportPackModal: {
        title: string;
        details: string;
        inputSubText: string;
        add: string;
      };
      cancelSubscriptionModal: TConfirmModalLang;
      restartSubscriptionModal: TConfirmModalLang;
    };
  };
  inputWorkspaceType: {
    title: string;
    tooltip: string;
  };
  inputParentTemplate: {
    title: string;
    tooltip: string;
  };
  workspaceTypeOptions: {
    CUSTOM: string;
    TEMPLATE: string;
    CHILD: string;
  };
  clientForm: TClientFormLang;
  clientStatusBanner: {
    subscriptionCancelDate: string;
    unableToProcessPayment: string;
  };
  addonsTab: {
    receiptConfiguration: {
      enableCustomReceiptConfiguration: string;
      receiptHeader: string;
    };
    inputOrderManagementConfig: TInputAttributeLang & {
      jsonMode: string;
      visualMode: string;
    };
  };
  alertsTab: TAlertsTabLang;
};

type TAlertsTabLang = {
  title: string;
  description: string;
  tenantBannerPrefix: string;
  tenantBannerEmphasis: string;
  tenantBannerSuffix: string;
  rulesConfiguredCount: {
    one: string;
    other: string;
  };
  createAlertButton: string;
  createModal: {
    title: string;
    subtitle: string;
    closeAriaLabel: string;
    addAction: string;
    comingSoonBadge: string;
  };
  emptyCatalogHint: {
    headingOne: string;
    headingOther: string;
    description: string;
    browseButton: string;
  };
  ruleCard: {
    enableAriaLabel: string;
    expandAriaLabel: string;
    collapseAriaLabel: string;
  };
  ruleEditor: {
    triggerRuleSection: string;
    alertBehaviorSection: string;
    severityLabel: string;
    removeButton: string;
    closeButton: string;
  };
  category: {
    FIELD_CRITICAL: string;
    PERFORMANCE: string;
    MOVEMENT: string;
    COVERAGE: string;
    OPTIMIZATION: string;
  };
  severity: {
    HIGH: string;
    MEDIUM: string;
    LOW: string;
  };
  weekdays: {
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
    sun: string;
  };
  occurrence: {
    MONTHLY: string;
  };
  types: {
    DAY_NOT_STARTED: TAlertTypeCopy;
    GPS_DISABLED: TAlertTypeCopy;
    NO_VISITS_AFTER_HOURS: TAlertTypeCopy;
    GAP_VS_DAILY_TARGET: TAlertTypeCopy;
    CUSTOMER_NOT_ENGAGED: TAlertTypeCopy;
    END_OF_DAY_REPORT: TAlertTypeCopy;
    ROUTE_NOT_RESPECTED: TAlertTypeCopy;
  };
  paramsSummary: {
    DAY_NOT_STARTED: string;
    GPS_DISABLED: string;
    NO_VISITS_AFTER_HOURS: string;
    GAP_VS_DAILY_TARGET: string;
    CUSTOMER_NOT_ENGAGED: string;
    END_OF_DAY_REPORT: string;
  };
  editors: {
    dayNotStarted: {
      description: string;
      cutoffTime: string;
      localTimezoneHelp: string;
      activeDays: string;
      hintTitle: string;
      hintBody: string;
    };
    gpsDisabled: {
      description: string;
      durationBeforeAlert: string;
      hoursSuffix: string;
      help: string;
    };
    noVisitsAfterHours: {
      description: string;
      inactivityThreshold: string;
      hoursSuffix: string;
      help: string;
    };
    gapVsDailyTarget: {
      description: string;
      gapThreshold: string;
      pctSuffix: string;
      evaluateAt: string;
      localTimezoneHelp: string;
    };
    customerNotEngaged: {
      description: string;
      notVisitedPeriod: string;
      daysSuffix: string;
      occurrence: string;
    };
    endOfDayReport: {
      description: string;
      htmlTemplate: string;
      placeholder: string;
      htmlRequired: string;
    };
  };
};

type TAlertTypeCopy = {
  title: string;
  description: string;
  summaryLabel: string;
};

export default TLang;
